import { useParams } from 'react-router-dom'
import { TableBody, TableCell, TableHead, TableRow, Box, Typography, Button, CircularProgress } from '@mui/material'
import VerifiedIcon from '@mui/icons-material/Verified'
import UndoIcon from '@mui/icons-material/Undo'

// components
import BankReconciliationSummaryCards from './BankReconciliationSummaryCards'
import { CustomTableCell } from '../../common/CustomTableCell'
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'
import { CardBox } from '../../../theme/styleComponents'
import { ContentHead } from '../../common/ContentHead'
import dayjs from 'dayjs'
import {
  FetchReconciliationQuery,
  ReconciliationDocumentFileFormat,
  useFetchPaymentsByProfitCenterLazyQuery,
  useFetchReconciliationLazyQuery
} from '../../../../generated'
import { useReconciliationDocument } from './useReconciliationDocument'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import { Alert } from '../../common/Alert'
import { AppContext } from '../../../context'
import { Fragment, useContext, useEffect, useState } from 'react'
import { ActionType } from '../../../context/AppContextReducer'
import { PaymentConciliationData } from '../../../interfaceTypes/props'

const DETAIL_HEADERS = [
  '',
  'Check No',
  'Reconciled At',
  'Amount',
  'Payment Type',
  'Slip #',
  'Client/Vendor',
  'Profit Center'
]

const PAYMENTS_BY_OFFICE_HEADERS = ['Date', 'Amount', 'Profit Center']

const ReconciliationPaymentsListing = () => {
  const { id: reconciliationId } = useParams()
  const { currentPage, dispatch } = useContext(AppContext)
  const [paymentsByDate, setPaymentsByDate] = useState<PaymentConciliationData[]>([])
  const [reconciliation, setReconciliation] = useState<FetchReconciliationQuery['fetchReconciliation']>()

  const [fetchGroupedByDatePayments, { loading: groupedPaymentsLoading }] = useFetchPaymentsByProfitCenterLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { fetchPaymentsByProfitCenter } = data || {}
      const { payments } = fetchPaymentsByProfitCenter || {}
      setPaymentsByDate(payments || [])

      dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 1 })
      dispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: 1 })
    }
  })

  const [fetchReconciliation, { refetch: refetchReconciliation, loading: reconciliationLoading }] =
    useFetchReconciliationLazyQuery({
      ...GRAPHQL_QUERY_POLICY,
      variables: undefined,
      onError(error) {
        Alert.error(error.message)
      },
      onCompleted(data) {
        const { fetchReconciliation } = data || {}
        setReconciliation(fetchReconciliation)
      }
    })

  // The PDF is generated server-side (same artefact the History list hands out) rather than by the
  // browser print dialog: it is the copy that goes to the outside accountants, so it must not depend
  // on the user's print settings or on print CSS holding up across browsers.
  const { download, isDownloading } = useReconciliationDocument()
  const numericReconciliationId = Number(reconciliationId)
  const downloadingPdf = isDownloading(numericReconciliationId, ReconciliationDocumentFileFormat.Pdf)

  useEffect(() => {
    !!reconciliation?.account.id &&
      !reconciliation?.unreconciledAt &&
      fetchGroupedByDatePayments({
        variables: {
          input: {
            monthOfYear: dayjs(reconciliation?.endDate).startOf('month').format('YYYY-MM-DD'),
            accountId: reconciliation?.account.id,
            reconciliationId: reconciliation.id,
            reconciled: true
          }
        }
      })
  }, [reconciliation])

  useEffect(() => {
    !!reconciliationId &&
      fetchReconciliation({
        variables: {
          id: Number(reconciliationId)
        }
      })
  }, [reconciliationId, currentPage])

  return (
    <CardBox marginTop={3}>
      <ContentHead
        title={`#${reconciliationId} Reconciliation ${reconciliation?.unreconciledAt ? '(Reverted)' : ''}`}
        backButton
        customButton={() => (
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              onClick={() => download(numericReconciliationId, ReconciliationDocumentFileFormat.Csv)}
              disabled={isDownloading(numericReconciliationId)}
            >
              DOWNLOAD CSV
            </Button>
            <Button
              variant="outlined"
              onClick={() => download(numericReconciliationId, ReconciliationDocumentFileFormat.Pdf)}
              disabled={isDownloading(numericReconciliationId)}
              startIcon={downloadingPdf ? <CircularProgress size={16} /> : undefined}
            >
              {downloadingPdf ? 'PREPARING PDF…' : 'DOWNLOAD PDF'}
            </Button>
          </Box>
        )}
      />
      <Box
        id="reconciliation-preview"
        sx={{
          '@media print': {
            boxShadow: 'none !important',
            margin: '0 !important',
            width: '100% !important',
            minHeight: '100% !important',
            pageBreakAfter: 'avoid',
            fontSize: '0.5em',
            '& .MuiTableContainer-root': {
              height: 'auto !important',
              maxHeight: 'none !important',
              overflow: 'visible !important'
            },
            '& table': {
              width: '100% !important',
              tableLayout: 'fixed !important'
            },
            '& td:first-of-type': {
              width: '40px !important',
              padding: '8px 0px !important'
            },
            '& td:nth-of-type(2), & td:nth-of-type(3), & td:nth-of-type(4), & td:nth-of-type(5)': {
              width: '12% !important'
            },
            '& td:nth-of-type(6), & td:nth-of-type(7)': {
              width: '26% !important'
            },
            '& .MuiTableCell-root': {
              padding: '4px 8px !important'
            },
            '& .MuiTypography-noWrap': {
              whiteSpace: 'normal !important',
              overflow: 'visible !important',
              textOverflow: 'unset !important',
              minWidth: 'unset !important'
            },
            '& .MuiTableContainer-root:last-child': {
              '& td:first-of-type, & th:first-of-type': {
                paddingLeft: '16px !important'
              }
            }
          }
        }}
      >
        {reconciliation && <BankReconciliationSummaryCards reconciliation={reconciliation} />}

        <Typography variant="h6" fontWeight={500} m={2}>
          Reconciled Payments
        </Typography>

        <Box sx={{ position: 'relative' }}>
          <StickyTable height="100%">
            <TableHead>
              <TableRow hover>
                {DETAIL_HEADERS.map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      '@media print': {
                        width: index === 0 ? '6% !important' : '12% !important'
                      }
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {reconciliationLoading ? (
              <TableSkeleton noOfRows={7} noOfCols={DETAIL_HEADERS.length} />
            ) : (
              <TableBody>
                {reconciliation?.payments?.map(payment => {
                  const {
                    id,
                    amount,
                    date,
                    office,
                    paymentType,
                    referenceNumber,
                    client,
                    vendor,
                    account,
                    reconciledAt,
                    slipNumber
                  } = payment
                  const clientName = client
                    ? `Client - ${client?.displayName?.trim() || ''} (#${client?.clientNumber})`
                    : ''
                  const vendorName = vendor ? `Vendor - ${vendor?.name} (#${vendor?.sortCode})` : ''
                  const officeName = `${office?.name} (#${office?.id})`

                  return (
                    <TableRow key={id} hover>
                      <TableCell>
                        {reconciliation.unreconciledAt ? <UndoIcon color="error" /> : <VerifiedIcon color="success" />}
                      </TableCell>
                      <CustomTableCell value={referenceNumber || '--'} />
                      <CustomTableCell value={dayjs(reconciledAt).format('MM/DD/YYYY')} />
                      <CustomTableCell value={`$${amount.toFixed(2)}`} />
                      <CustomTableCell value={paymentType || '--'} />
                      <CustomTableCell value={slipNumber || '--'} />
                      <CustomTableCell value={clientName || vendorName || '--'} preserveCase />
                      <CustomTableCell value={office?.id ? officeName : '--'} />
                    </TableRow>
                  )
                })}
              </TableBody>
            )}
          </StickyTable>

          {!reconciliation?.unreconciledAt && (
            <Fragment>
              <Typography variant="h6" fontWeight={500} mx={2} my={4}>
                Payments By Profit Center
              </Typography>

              <StickyTable height="360px">
                <TableHead>
                  <TableRow hover>
                    {PAYMENTS_BY_OFFICE_HEADERS.map((columnName, index) => (
                      <TableCell key={index} component="th" align={'left'}>
                        {columnName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {groupedPaymentsLoading ? (
                  <TableSkeleton noOfRows={7} noOfCols={PAYMENTS_BY_OFFICE_HEADERS.length} />
                ) : (
                  <TableBody>
                    {paymentsByDate?.map(payment => {
                      const { id, amount, date, officeId, officeName } = payment
                      return (
                        <TableRow key={id} hover>
                          <CustomTableCell value={dayjs(date).format('MM/DD/YYYY')} />
                          <CustomTableCell value={`$${amount.toFixed(2)}`} />
                          <CustomTableCell value={officeName && officeId ? `${officeName} (#${officeId})` : '--'} />
                        </TableRow>
                      )
                    })}
                  </TableBody>
                )}
              </StickyTable>
            </Fragment>
          )}
        </Box>
      </Box>
    </CardBox>
  )
}

export default ReconciliationPaymentsListing
